import React from 'react';
import {
    findVerticalScrollParent,
    HIDDEN_OVERLAY_STATE,
    OVERLAY_EPSILON,
    OverlayDescriptor,
    OverlayState,
    resolveGroupOverlay,
} from './group-overlay-shared';
import {useLayoutState} from '../../../../../contexts/layout-context';

/**
 * Payload dispatched by group row component when expanded/collapsed state changes.
 */
type GroupOpenChangeDetail = {
    key: string;
    level: number;
    open: boolean;
    showButton: boolean;
};

type CachedGroupElements = {
    key: string;
    root: HTMLElement;
    header: HTMLElement;
};

/**
 * Desktop overlay hook input.
 */
type Params<TGroup> = {
    isMobile: boolean;
    topLevelGroupMap: Map<string, TGroup>;
    onDebugLog: (event: string, payload?: unknown) => void;
    onWriteDiag: (payload: Record<string, unknown>) => void;
};

/**
 * Desktop overlay hook output refs/state/actions.
 */
type Result = {
    gridWrapperRef: React.MutableRefObject<HTMLDivElement | null>;
    customHeaderRef: React.MutableRefObject<HTMLDivElement | null>;
    overlayRef: React.MutableRefObject<HTMLDivElement | null>;
    overlayState: OverlayState;
    isLayoutTransitioning: boolean;
    overlayActiveKeyRef: React.MutableRefObject<string | null>;
    setOverlayHidden: (reason: string) => void;
    recalculateOverlay: () => void;
    scheduleOverlayRecalculation: () => void;
    handleOverlayCollapseActiveGroup: () => void;
};

const GROUP_OPEN_CHANGE_EVENT = 'timesheet-group-open-change';

export const useDesktopGroupOverlay = <TGroup>({
    isMobile,
    topLevelGroupMap,
    onDebugLog,
    onWriteDiag
}: Params<TGroup>): Result => {
    const { desktopMenuPhase, desktopMenuOpen } = useLayoutState();
    const gridWrapperRef = React.useRef<HTMLDivElement>(null);
    const customHeaderRef = React.useRef<HTMLDivElement>(null);
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const overlayRafRef = React.useRef<number | null>(null);
    const overlayScrollLoopRafRef = React.useRef<number | null>(null);
    const overlayLayoutLoopRafRef = React.useRef<number | null>(null);
    const overlayLastScrollAtRef = React.useRef<number>(0);
    const overlayActiveKeyRef = React.useRef<string | null>(null);
    const overlayHeightRef = React.useRef<number>(40);
    const groupElementsRef = React.useRef<CachedGroupElements[]>([]);
    const desktopMenuPhaseRef = React.useRef(desktopMenuPhase);
    const prevCandidateKeysRef = React.useRef<Set<string>>(new Set());
    const prevAnchoredKeysRef = React.useRef<Set<string>>(new Set());
    const prevOverlayHiddenReasonRef = React.useRef<string | null>(null);
    const prevRecalcSignatureRef = React.useRef<string | null>(null);
    const [overlayState, setOverlayState] = React.useState<OverlayState>(HIDDEN_OVERLAY_STATE);

    /**
     * Applies geometry directly to the mounted overlay element so layout-following
     * stays in lockstep with CSS-driven menu width transitions.
     */
    const applyOverlayGeometry = React.useCallback((nextState: OverlayState) => {
        const overlayElement = overlayRef.current;
        if (!overlayElement || nextState.activeKey === null) {
            return;
        }

        overlayElement.style.transform = `translateY(${nextState.offsetY}px)`;
    }, []);

    /**
     * Caches mounted top-level group roots and headers so scroll-time recalculation
     * only performs geometry reads instead of repeated DOM queries.
     */
    const refreshGroupElements = React.useCallback((): CachedGroupElements[] => {
        const wrapperElement = gridWrapperRef.current;
        if (!wrapperElement) {
            groupElementsRef.current = [];
            return [];
        }

        const nextElements = Array
            .from(wrapperElement.querySelectorAll<HTMLElement>('[data-grid-group-root="true"][data-grid-group-level="0"]'))
            .map((root) => {
                const key = root.dataset.gridGroupKey ?? '';
                const header = root.querySelector<HTMLElement>('[data-grid-group-header="true"][data-grid-group-level="0"]');
                if (!key || !header) {
                    return null;
                }

                return {
                    key,
                    root,
                    header,
                };
            })
            .filter((entry): entry is CachedGroupElements => entry !== null);

        groupElementsRef.current = nextElements;
        return nextElements;
    }, []);

    /**
     * Returns cached group nodes when still attached to the current wrapper.
     */
    const getGroupElements = React.useCallback((): CachedGroupElements[] => {
        const wrapperElement = gridWrapperRef.current;
        if (!wrapperElement) {
            return [];
        }

        const cachedElements = groupElementsRef.current;
        const isCacheValid = cachedElements.length > 0
            && cachedElements.every((entry) => (
                entry.root.isConnected
                && entry.header.isConnected
                && wrapperElement.contains(entry.root)
                && entry.root.dataset.gridGroupKey === entry.key
            ));

        return isCacheValid ? cachedElements : refreshGroupElements();
    }, [refreshGroupElements]);

    /**
     * Tracks the current overlay height to avoid measuring it on every scroll frame.
     */
    const updateOverlayHeight = React.useCallback(() => {
        const nextHeight = overlayRef.current?.getBoundingClientRect().height;
        if (nextHeight && nextHeight > 0) {
            overlayHeightRef.current = nextHeight;
        }
    }, []);

    /**
     * Hides overlay state and records a reason for diagnostics.
     */
    const setOverlayHidden = React.useCallback((reason: string) => {
        if (prevOverlayHiddenReasonRef.current !== reason) {
            onDebugLog('overlay:hidden-request', { reason });
            prevOverlayHiddenReasonRef.current = reason;
        }
        onWriteDiag({
            phase: 'hidden',
            hiddenReason: reason,
        });

        setOverlayState((state) => {
            if (state.activeKey !== null) {
                onDebugLog('overlay:hidden', {
                    reason,
                    activeKey: state.activeKey,
                });
                onWriteDiag({
                    phase: 'hidden-active',
                    hiddenReason: reason,
                    activeKey: state.activeKey,
                });
            }
            return state.activeKey === null ? state : HIDDEN_OVERLAY_STATE;
        });
    }, [onDebugLog, onWriteDiag]);

    /**
     * Emits enter/exit debug events for tracked group key sets.
     */
    const syncTrackedGroupKeys = React.useCallback((
        nextKeys: Set<string>,
        previousRef: React.MutableRefObject<Set<string>>,
        enterEvent: string,
        exitEvent: string,
        payload?: Record<string, unknown>
    ) => {
        const previousKeys = previousRef.current;
        nextKeys.forEach((key) => {
            if (!previousKeys.has(key)) {
                onDebugLog(enterEvent, { key, ...payload });
            }
        });
        previousKeys.forEach((key) => {
            if (!nextKeys.has(key)) {
                onDebugLog(exitEvent, { key, ...payload });
            }
        });
        previousRef.current = nextKeys;
    }, [onDebugLog]);

    /**
     * Clears tracked sets when overlay cannot be computed.
     */
    const clearTrackedGroupKeys = React.useCallback((reason: string) => {
        syncTrackedGroupKeys(
            new Set(),
            prevCandidateKeysRef,
            'group:sticky-zone-enter',
            'group:sticky-zone-exit',
            { reason }
        );
        syncTrackedGroupKeys(
            new Set(),
            prevAnchoredKeysRef,
            'group:header-cross-sticky-line',
            'group:header-leave-sticky-line',
            { reason }
        );
    }, [syncTrackedGroupKeys]);

    /**
     * Recomputes active desktop overlay candidate from DOM geometry.
     */
    const recalculateOverlay = React.useCallback(() => {
        if (isMobile) {
            clearTrackedGroupKeys('mobile-mode');
            setOverlayHidden('mobile-mode');
            return;
        }

        const wrapperElement = gridWrapperRef.current;
        const headerElement = customHeaderRef.current;
        if (!wrapperElement || !headerElement) {
            clearTrackedGroupKeys('missing-wrapper-or-header');
            setOverlayHidden('missing-wrapper-or-header');
            return;
        }

        const rootGroups = getGroupElements();

        if (rootGroups.length < 1) {
            clearTrackedGroupKeys('no-top-level-groups');
            setOverlayHidden('no-top-level-groups');
            return;
        }

        const stickyTop = headerElement.getBoundingClientRect().bottom;
        const descriptors = rootGroups
            .map((element) => {
                return {
                    key: element.key,
                    isOpen: element.root.dataset.groupOpen === 'true',
                    headerTop: element.header.getBoundingClientRect().top,
                    groupBottom: element.root.getBoundingClientRect().bottom,
                };
            })
            .filter((value): value is OverlayDescriptor => value !== null);

        const matchedDescriptors = descriptors.filter((descriptor) => topLevelGroupMap.has(descriptor.key));
        const stickyTopRounded = Number(stickyTop.toFixed(2));
        const recalcDiag = {
            rootGroups: rootGroups.length,
            descriptors: descriptors.length,
            matchedDescriptors: matchedDescriptors.length,
            topLevelGroups: topLevelGroupMap.size,
            stickyTop: stickyTopRounded,
            sampleDescriptorKeys: descriptors.slice(0, 5).map((descriptor) => descriptor.key),
            sampleTopLevelKeys: Array.from(topLevelGroupMap.keys()).slice(0, 5),
        };
        onWriteDiag({
            phase: 'recalc',
            ...recalcDiag,
        });

        const {
            candidates,
            anchoredCandidates,
            activeCandidate,
            nextOpenCandidate,
        } = resolveGroupOverlay({
            descriptors,
            stickyTop,
            overlayHeight: overlayHeightRef.current,
            // Desktop overlay should only track top-level groups existing in current grouped data.
            isDescriptorEligible: (descriptor) => topLevelGroupMap.has(descriptor.key),
        });
        const availableHeightUntilNextGroup = nextOpenCandidate
            ? nextOpenCandidate.headerTop - stickyTop
            : Number.POSITIVE_INFINITY;
        const availableHeightUntilGroupEnd = activeCandidate
            ? activeCandidate.groupBottom - stickyTop
            : 0;
        const openGroupKeys = new Set(descriptors.filter((descriptor) => descriptor.isOpen).map((descriptor) => descriptor.key));
        const currentActiveKey = overlayActiveKeyRef.current;

        if (currentActiveKey && !openGroupKeys.has(currentActiveKey)) {
            onDebugLog('overlay:active-group-collapsed-recalc-continue', {
                activeKey: currentActiveKey,
            });
        }

        const candidateKeys = new Set(candidates.map((descriptor) => descriptor.key));
        syncTrackedGroupKeys(
            candidateKeys,
            prevCandidateKeysRef,
            'group:sticky-zone-enter',
            'group:sticky-zone-exit',
            { stickyTop: stickyTopRounded }
        );

        if (candidates.length < 1) {
            syncTrackedGroupKeys(
                new Set(),
                prevAnchoredKeysRef,
                'group:header-cross-sticky-line',
                'group:header-leave-sticky-line',
                { stickyTop: stickyTopRounded }
            );
            setOverlayHidden('no-active-candidates');
            return;
        }

        const recalcSignature = JSON.stringify({
            rootGroups: rootGroups.length,
            descriptors: descriptors.length,
            matchedDescriptors: matchedDescriptors.length,
            candidates: candidates.length,
            anchored: anchoredCandidates.length,
            active: anchoredCandidates.length > 0 ? anchoredCandidates[anchoredCandidates.length - 1]?.key : null,
            stickyTop: stickyTopRounded,
        });
        if (prevRecalcSignatureRef.current !== recalcSignature) {
            onDebugLog('overlay:recalc', {
                ...recalcDiag,
                candidates: candidates.length,
                anchoredCandidates: anchoredCandidates.length,
                firstCandidate: candidates[0]?.key ?? null,
                firstAnchored: anchoredCandidates[0]?.key ?? null,
            });
            onWriteDiag({
                phase: 'recalc-signature',
                candidates: candidates.length,
                anchoredCandidates: anchoredCandidates.length,
                firstCandidate: candidates[0]?.key ?? null,
                firstAnchored: anchoredCandidates[0]?.key ?? null,
            });
            prevRecalcSignatureRef.current = recalcSignature;
        }
        syncTrackedGroupKeys(
            new Set(anchoredCandidates.map((descriptor) => descriptor.key)),
            prevAnchoredKeysRef,
            'group:header-cross-sticky-line',
            'group:header-leave-sticky-line',
            { stickyTop: stickyTopRounded }
        );

        if (anchoredCandidates.length < 1) {
            setOverlayHidden('waiting-header-cross');
            return;
        }
        if (!activeCandidate) {
            setOverlayHidden('waiting-header-cross-active-null');
            return;
        }
        if (availableHeightUntilNextGroup < overlayHeightRef.current) {
            setOverlayHidden('next-group-too-close-for-full-overlay');
            return;
        }
        if (availableHeightUntilGroupEnd < overlayHeightRef.current) {
            setOverlayHidden('active-group-too-short-for-full-overlay');
            return;
        }

        const wrapperRect = wrapperElement.getBoundingClientRect();

        setOverlayState((state) => {
            const nextState: OverlayState = {
                activeKey: activeCandidate.key,
                top: stickyTop,
                left: wrapperRect.left,
                width: wrapperRect.width,
                // Keep the overlay fully visible under the sticky header.
                offsetY: 0,
            };

            const isSameState = state.activeKey === nextState.activeKey
                && Math.abs(state.offsetY - nextState.offsetY) < OVERLAY_EPSILON;

            applyOverlayGeometry(nextState);

            if (!isSameState) {
                if (state.activeKey !== nextState.activeKey) {
                    onDebugLog('overlay:active-group-changed', {
                        from: state.activeKey,
                        to: nextState.activeKey,
                        stickyTop: Number(stickyTop.toFixed(2)),
                        nextHeaderTop: nextOpenCandidate ? Number(nextOpenCandidate.headerTop.toFixed(2)) : null,
                    });
                }
            }

            return isSameState ? state : nextState;
        });
    }, [applyOverlayGeometry, clearTrackedGroupKeys, getGroupElements, isMobile, onDebugLog, onWriteDiag, setOverlayHidden, syncTrackedGroupKeys, topLevelGroupMap]);

    /**
     * Schedules one recomputation per animation frame.
     */
    const scheduleOverlayRecalculation = React.useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (overlayRafRef.current !== null) {
            return;
        }

        overlayRafRef.current = window.requestAnimationFrame(() => {
            overlayRafRef.current = null;
            recalculateOverlay();
        });
    }, [recalculateOverlay]);

    /**
     * Collapses currently active group by clicking its original group toggle.
     */
    const handleOverlayCollapseActiveGroup = React.useCallback(() => {
        const activeKey = overlayState.activeKey;
        if (!activeKey) {
            return;
        }

        const wrapperElement = gridWrapperRef.current;
        if (!wrapperElement) {
            onDebugLog('overlay:collapse-click-skip', { reason: 'missing-wrapper', activeKey });
            return;
        }

        const toggles = Array.from(
            wrapperElement.querySelectorAll<HTMLElement>('[data-grid-group-toggle="true"][data-grid-group-level="0"]')
        );
        const targetToggle = toggles.find((toggle) => toggle.dataset.gridGroupKey === activeKey);
        const targetButton = targetToggle?.querySelector<HTMLButtonElement>('button');

        if (!targetButton) {
            onDebugLog('overlay:collapse-click-skip', { reason: 'toggle-not-found', activeKey });
            return;
        }

        onDebugLog('overlay:collapse-click', { activeKey });
        targetButton.click();
        scheduleOverlayRecalculation();
    }, [onDebugLog, overlayState.activeKey, scheduleOverlayRecalculation]);

    /**
     * Keeps overlay synchronized during momentum/inertial scroll.
     */
    const startOverlayScrollSyncLoop = React.useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (overlayScrollLoopRafRef.current !== null) {
            return;
        }

        const loop = () => {
            overlayScrollLoopRafRef.current = null;
            recalculateOverlay();

            const now = window.performance.now();
            if (now - overlayLastScrollAtRef.current < 140) {
                overlayScrollLoopRafRef.current = window.requestAnimationFrame(loop);
            }
        };

        overlayScrollLoopRafRef.current = window.requestAnimationFrame(loop);
    }, [recalculateOverlay]);

    const startOverlayLayoutSyncLoop = React.useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (overlayLayoutLoopRafRef.current !== null) {
            return;
        }

        const loop = () => {
            overlayLayoutLoopRafRef.current = null;
            recalculateOverlay();

            if (desktopMenuPhaseRef.current === 'opening' || desktopMenuPhaseRef.current === 'closing') {
                overlayLayoutLoopRafRef.current = window.requestAnimationFrame(loop);
            }
        };

        overlayLayoutLoopRafRef.current = window.requestAnimationFrame(loop);
    }, [recalculateOverlay]);

    React.useLayoutEffect(() => {
        overlayActiveKeyRef.current = overlayState.activeKey;
    }, [overlayState.activeKey]);

    React.useLayoutEffect(() => {
        desktopMenuPhaseRef.current = desktopMenuPhase;
    }, [desktopMenuPhase]);

    React.useEffect(() => {
        return () => {
            if (overlayRafRef.current !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(overlayRafRef.current);
            }
            if (overlayScrollLoopRafRef.current !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(overlayScrollLoopRafRef.current);
            }
            if (overlayLayoutLoopRafRef.current !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(overlayLayoutLoopRafRef.current);
            }
            onDebugLog('overlay:raf-cleanup');
        };
    }, [onDebugLog]);

    React.useLayoutEffect(() => {
        if (isMobile) {
            return;
        }

        if (desktopMenuPhase === 'opening' || desktopMenuPhase === 'closing') {
            onDebugLog('overlay:layout-transition-sync-start', {
                desktopMenuPhase,
                desktopMenuOpen,
            });
            recalculateOverlay();
            scheduleOverlayRecalculation();
            startOverlayLayoutSyncLoop();
            return;
        }

        if (overlayLayoutLoopRafRef.current !== null && typeof window !== 'undefined') {
            window.cancelAnimationFrame(overlayLayoutLoopRafRef.current);
            overlayLayoutLoopRafRef.current = null;
        }
        onDebugLog('overlay:layout-transition-sync-end', {
            desktopMenuPhase,
            desktopMenuOpen,
        });
        recalculateOverlay();
        scheduleOverlayRecalculation();
    }, [
        desktopMenuOpen,
        desktopMenuPhase,
        isMobile,
        onDebugLog,
        recalculateOverlay,
        scheduleOverlayRecalculation,
        startOverlayLayoutSyncLoop,
    ]);

    React.useEffect(() => {
        if (isMobile) {
            setOverlayHidden('effect-mobile-cleanup');
            return;
        }

        const wrapperElement = gridWrapperRef.current;
        if (!wrapperElement) {
            onDebugLog('overlay:init-skip', { reason: 'missing-grid-wrapper' });
            return;
        }

        refreshGroupElements();
        updateOverlayHeight();

        const scrollContainer = findVerticalScrollParent(wrapperElement);
        const handlePositionChange = () => {
            if (typeof window !== 'undefined') {
                overlayLastScrollAtRef.current = window.performance.now();
            }
            startOverlayScrollSyncLoop();
            scheduleOverlayRecalculation();
        };
        const handleGroupToggleClick = (event: Event) => {
            const target = event.target as HTMLElement | null;
            if (!target) {
                return;
            }

            const toggle = target.closest<HTMLElement>('[data-grid-group-toggle="true"][data-grid-group-level="0"]');
            if (!toggle) {
                return;
            }

            const key = toggle.dataset.gridGroupKey ?? null;
            const activeKey = overlayActiveKeyRef.current;
            if (!key || !activeKey || key !== activeKey) {
                return;
            }

            onDebugLog('overlay:active-group-toggle-click', { key });
            setOverlayHidden('active-group-toggle-click');
            recalculateOverlay();
            scheduleOverlayRecalculation();
        };
        const handleGroupOpenChange = (event: Event) => {
            const detail = (event as CustomEvent<GroupOpenChangeDetail>).detail;
            if (!detail || detail.level !== 0) {
                return;
            }

            onDebugLog('overlay:group-open-change-event', detail);
            const activeKey = overlayActiveKeyRef.current;
            if (activeKey && !detail.open && detail.key === activeKey) {
                setOverlayHidden('active-group-collapsed-event');
            }

            recalculateOverlay();
            scheduleOverlayRecalculation();
        };

        scrollContainer?.addEventListener('scroll', handlePositionChange, { passive: true });
        document.addEventListener('scroll', handlePositionChange, { passive: true, capture: true });
        window.addEventListener('scroll', handlePositionChange, { passive: true, capture: true });
        window.addEventListener('resize', handlePositionChange);
        window.addEventListener(GROUP_OPEN_CHANGE_EVENT, handleGroupOpenChange as EventListener);
        wrapperElement.addEventListener('click', handleGroupToggleClick, true);
        onDebugLog('overlay:listeners-attached', {
            hasScrollContainer: !!scrollContainer,
            scrollContainerClass: scrollContainer?.className ?? null,
        });

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => {
                updateOverlayHeight();
                scheduleOverlayRecalculation();
            })
            : null;
        if (resizeObserver) {
            resizeObserver.observe(wrapperElement);
            if (customHeaderRef.current) {
                resizeObserver.observe(customHeaderRef.current);
            }
            if (scrollContainer) {
                resizeObserver.observe(scrollContainer);
            }
            if (overlayRef.current) {
                resizeObserver.observe(overlayRef.current);
            }
        }

        const mutationObserver = typeof MutationObserver !== 'undefined'
            ? new MutationObserver(() => {
                refreshGroupElements();
                updateOverlayHeight();
                scheduleOverlayRecalculation();
            })
            : null;
        mutationObserver?.observe(wrapperElement, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['data-group-open'],
        });

        onDebugLog('overlay:initial-recalc-sync');
        recalculateOverlay();
        scheduleOverlayRecalculation();

        return () => {
            scrollContainer?.removeEventListener('scroll', handlePositionChange);
            document.removeEventListener('scroll', handlePositionChange, true);
            window.removeEventListener('scroll', handlePositionChange, true);
            window.removeEventListener('resize', handlePositionChange);
            window.removeEventListener(GROUP_OPEN_CHANGE_EVENT, handleGroupOpenChange as EventListener);
            wrapperElement.removeEventListener('click', handleGroupToggleClick, true);
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            if (overlayScrollLoopRafRef.current !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(overlayScrollLoopRafRef.current);
                overlayScrollLoopRafRef.current = null;
            }
            groupElementsRef.current = [];
            onDebugLog('overlay:listeners-detached');
        };
    }, [
        isMobile,
        onDebugLog,
        recalculateOverlay,
        refreshGroupElements,
        scheduleOverlayRecalculation,
        setOverlayHidden,
        startOverlayScrollSyncLoop,
        updateOverlayHeight
    ]);

    return {
        gridWrapperRef,
        customHeaderRef,
        overlayRef,
        overlayState,
        isLayoutTransitioning: desktopMenuPhase === 'opening' || desktopMenuPhase === 'closing',
        overlayActiveKeyRef,
        setOverlayHidden,
        recalculateOverlay,
        scheduleOverlayRecalculation,
        handleOverlayCollapseActiveGroup,
    };
};
